/**
 * Intelligent File Analyzer Service
 * Redesigned for maximum efficiency, accuracy, and speed with Gemini API
 * 
 * Key Features:
 * - Smart content extraction and preprocessing
 * - Intelligent caching to avoid re-analysis
 * - Progressive loading for large files
 * - Optimized prompts for Gemini 2.0 Flash
 * - Parallel processing for multiple attachments
 * - Adaptive quality based on file type and size
 */

import { GoogleGenerativeAI } from "@google/generative-ai";

export interface FileAnalysisResult {
    summary: string;
    extractedText?: string;
    detectedType: 'document' | 'image' | 'diagram' | 'chart' | 'table' | 'code' | 'handwriting' | 'mixed' | 'presentation';
    confidence: number;
    keyEntities: string[];
    suggestedQuestions: string[];
    slideCount?: number; // For presentations
    metadata: {
        fileSize: number;
        mimeType: string;
        processingTime: number;
        cacheHit: boolean;
        isPremiumFileType?: boolean; // For file types that cost extra
        tokenMultiplier?: number; // Extra tokens for complex files
    };
}

export interface FilePreprocessingResult {
    optimizedData: string;
    originalSize: number;
    optimizedSize: number;
    compressionRatio: number;
    contentType: string;
    shouldCache: boolean;
}

/**
 * Advanced caching system for analyzed files
 */
class FileAnalysisCache {
    private cache: Map<string, { result: FileAnalysisResult; timestamp: number }>;
    private readonly MAX_CACHE_SIZE = 50;
    private readonly CACHE_TTL = 30 * 60 * 1000; // 30 minutes

    constructor() {
        this.cache = new Map();
        this.loadFromStorage();
    }

    private generateKey(data: string, mimeType: string): string {
        // Use first 1000 chars + last 1000 chars for hash to avoid processing entire file
        const sample = data.length > 2000 
            ? data.substring(0, 1000) + data.substring(data.length - 1000)
            : data;
        return `${mimeType}:${this.simpleHash(sample)}`;
    }

    private simpleHash(str: string): string {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash).toString(36);
    }

    get(data: string, mimeType: string): FileAnalysisResult | null {
        const key = this.generateKey(data, mimeType);
        const cached = this.cache.get(key);
        
        if (!cached) return null;
        
        // Check if expired
        if (Date.now() - cached.timestamp > this.CACHE_TTL) {
            this.cache.delete(key);
            return null;
        }
        
        console.log('[FileAnalyzer] Cache hit for file');
        return { ...cached.result, metadata: { ...cached.result.metadata, cacheHit: true } };
    }

    set(data: string, mimeType: string, result: FileAnalysisResult): void {
        const key = this.generateKey(data, mimeType);
        
        // Implement LRU eviction
        if (this.cache.size >= this.MAX_CACHE_SIZE) {
            const firstKey = this.cache.keys().next().value;
            this.cache.delete(firstKey);
        }
        
        this.cache.set(key, {
            result,
            timestamp: Date.now()
        });
        
        this.saveToStorage();
    }

    private loadFromStorage(): void {
        try {
            const stored = localStorage.getItem('file_analysis_cache');
            if (stored) {
                const data = JSON.parse(stored);
                this.cache = new Map(data);
                console.log(`[FileAnalyzer] Loaded ${this.cache.size} cached analyses`);
            }
        } catch (error) {
            console.error('[FileAnalyzer] Failed to load cache:', error);
        }
    }

    private saveToStorage(): void {
        try {
            const data = Array.from(this.cache.entries());
            localStorage.setItem('file_analysis_cache', JSON.stringify(data));
        } catch (error) {
            console.error('[FileAnalyzer] Failed to save cache:', error);
        }
    }

    clear(): void {
        this.cache.clear();
        localStorage.removeItem('file_analysis_cache');
    }
}

const analysisCache = new FileAnalysisCache();

/**
 * Smart file preprocessing - optimizes files before sending to Gemini
 */
export async function preprocessFile(
    data: string,
    mimeType: string,
    fileName: string
): Promise<FilePreprocessingResult> {
    const startTime = performance.now();
    const originalSize = data.length;
    
    let optimizedData = data;
    let contentType = 'unknown';
    let shouldCache = true;

    // Image optimization
    if (mimeType.startsWith('image/')) {
        contentType = 'image';
        
        // For images, we can use Gemini's native image understanding
        // No need for additional preprocessing
        optimizedData = data;
        
        // Don't cache very large images
        if (originalSize > 500000) {
            shouldCache = false;
        }
    }
    
    // PDF optimization
    else if (mimeType === 'application/pdf') {
        contentType = 'pdf';
        optimizedData = data;
        
        // PDFs are good candidates for caching
        shouldCache = true;
    }
    
    // PowerPoint optimization (.pptx, .ppt)
    else if (
        mimeType === 'application/vnd.ms-powerpoint' || 
        mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ) {
        contentType = 'presentation';
        optimizedData = data;
        
        // Presentations are good candidates for caching
        shouldCache = true;
        console.log(`[FileAnalyzer] Processing PowerPoint file: ${fileName}`);
    }
    
    const optimizedSize = optimizedData.length;
    const compressionRatio = originalSize > 0 ? optimizedSize / originalSize : 1;
    const processingTime = performance.now() - startTime;
    
    console.log(`[FileAnalyzer] Preprocessed ${fileName}: ${(originalSize / 1024).toFixed(1)}KB → ${(optimizedSize / 1024).toFixed(1)}KB (${(compressionRatio * 100).toFixed(1)}%) in ${processingTime.toFixed(0)}ms`);
    
    return {
        optimizedData,
        originalSize,
        optimizedSize,
        compressionRatio,
        contentType,
        shouldCache
    };
}

/**
 * Analyze file with intelligent caching and optimized prompts
 */
export async function analyzeFileIntelligently(
    data: string,
    mimeType: string,
    fileName: string,
    userQuestion?: string,
    apiKey?: string
): Promise<FileAnalysisResult> {
    const startTime = performance.now();
    
    // Check cache first
    const cached = analysisCache.get(data, mimeType);
    if (cached) {
        return cached;
    }
    
    // Preprocess file
    const preprocessed = await preprocessFile(data, mimeType, fileName);
    
    // Initialize Gemini
    const DEFAULT_API_KEY = 'AIzaSyDfI0ey0RYiNcQm9LBXRoJgP5rbPah6FtI';
    const effectiveApiKey = apiKey || DEFAULT_API_KEY;
    const ai = new GoogleGenerativeAI(effectiveApiKey);
    
    // Use Gemini 2.5 Flash Lite for speed and efficiency
    const model = ai.getGenerativeModel({
        model: 'gemini-2.5-flash-lite',
        generationConfig: {
            temperature: 0.1, // Low temperature for factual analysis
            topK: 20,
            topP: 0.8,
            maxOutputTokens: 1024, // Limit output for faster response
        }
    });
    
    // Craft optimized analysis prompt
    const analysisPrompt = createAnalysisPrompt(fileName, mimeType, userQuestion);
    
    try {
        // Send for analysis
        const result = await model.generateContent([
            { text: analysisPrompt },
            { inlineData: { mimeType, data: preprocessed.optimizedData } }
        ]);
        
        const response = result.response.text();
        const analysisResult = parseAnalysisResponse(response, {
            fileSize: preprocessed.originalSize,
            mimeType,
            processingTime: performance.now() - startTime,
            cacheHit: false
        });
        
        // Cache the result if appropriate
        if (preprocessed.shouldCache) {
            analysisCache.set(data, mimeType, analysisResult);
        }
        
        console.log(`[FileAnalyzer] Analysis completed in ${analysisResult.metadata.processingTime.toFixed(0)}ms`);
        
        return analysisResult;
        
    } catch (error) {
        console.error('[FileAnalyzer] Analysis failed:', error);
        
        // Return fallback result
        return {
            summary: `Unable to analyze ${fileName}. ${error instanceof Error ? error.message : 'Unknown error'}`,
            detectedType: 'mixed',
            confidence: 0,
            keyEntities: [],
            suggestedQuestions: [],
            metadata: {
                fileSize: preprocessed.originalSize,
                mimeType,
                processingTime: performance.now() - startTime,
                cacheHit: false
            }
        };
    }
}

/**
 * Batch analyze multiple files in parallel
 */
export async function analyzeMultipleFiles(
    files: Array<{ data: string; mimeType: string; fileName: string }>,
    userQuestion?: string,
    apiKey?: string
): Promise<FileAnalysisResult[]> {
    console.log(`[FileAnalyzer] Batch analyzing ${files.length} files`);
    
    // Process files in parallel for speed
    const promises = files.map(file => 
        analyzeFileIntelligently(file.data, file.mimeType, file.fileName, userQuestion, apiKey)
    );
    
    return Promise.all(promises);
}

/**
 * Create optimized analysis prompt based on file type
 */
function createAnalysisPrompt(fileName: string, mimeType: string, userQuestion?: string): string {
    const basePrompt = `Analyze this file (${fileName}) quickly and accurately. Provide:

1. SUMMARY: Brief 1-2 sentence description of content
2. TYPE: Classify as one of: document, image, diagram, chart, table, code, handwriting, mixed
3. CONFIDENCE: Your confidence level (0-100)
4. KEY_ENTITIES: List 3-5 main topics, concepts, or subjects
5. QUESTIONS: Suggest 2-3 relevant questions someone might ask

Format your response EXACTLY as:
SUMMARY: [your summary]
TYPE: [type]
CONFIDENCE: [number]
KEY_ENTITIES: [entity1], [entity2], [entity3]
QUESTIONS: [q1] | [q2] | [q3]`;

    // Add context if user provided a question
    if (userQuestion) {
        return `${basePrompt}

USER QUESTION: ${userQuestion}
Focus your analysis on information relevant to answering this question.`;
    }
    
    // Add file-type specific instructions
    if (mimeType.startsWith('image/')) {
        return `${basePrompt}

This is an image. Identify visual elements, text (if any), diagrams, charts, or other content.`;
    } else if (mimeType === 'application/pdf') {
        return `${basePrompt}

This is a PDF document. Extract key information, structure, and main topics.`;
    } else if (
        mimeType === 'application/vnd.ms-powerpoint' || 
        mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ) {
        return `${basePrompt}

This is a PowerPoint presentation. Analyze:
- Slide structure and flow
- Key points from each slide
- Visual elements (charts, diagrams, images)
- Main themes and topics covered
- Any accounting/CPA-related content (PFRS, GAAP, tax, auditing concepts)

Also provide:
SLIDE_COUNT: [estimated number of slides]`;
    }
    
    return basePrompt;
}

/**
 * Parse AI response into structured result
 */
function parseAnalysisResponse(
    response: string,
    metadata: FileAnalysisResult['metadata']
): FileAnalysisResult {
    const lines = response.split('\n').map(l => l.trim()).filter(l => l);
    
    let summary = '';
    let detectedType: FileAnalysisResult['detectedType'] = 'mixed';
    let confidence = 75; // Default confidence
    let keyEntities: string[] = [];
    let suggestedQuestions: string[] = [];
    let slideCount: number | undefined;
    
    for (const line of lines) {
        if (line.startsWith('SUMMARY:')) {
            summary = line.replace('SUMMARY:', '').trim();
        } else if (line.startsWith('TYPE:')) {
            const type = line.replace('TYPE:', '').trim().toLowerCase();
            if (['document', 'image', 'diagram', 'chart', 'table', 'code', 'handwriting', 'mixed', 'presentation'].includes(type)) {
                detectedType = type as FileAnalysisResult['detectedType'];
            }
        } else if (line.startsWith('CONFIDENCE:')) {
            const conf = parseInt(line.replace('CONFIDENCE:', '').trim());
            if (!isNaN(conf)) confidence = Math.min(100, Math.max(0, conf));
        } else if (line.startsWith('KEY_ENTITIES:')) {
            const entities = line.replace('KEY_ENTITIES:', '').trim();
            keyEntities = entities.split(',').map(e => e.trim()).filter(e => e);
        } else if (line.startsWith('QUESTIONS:')) {
            const questions = line.replace('QUESTIONS:', '').trim();
            suggestedQuestions = questions.split('|').map(q => q.trim()).filter(q => q);
        } else if (line.startsWith('SLIDE_COUNT:')) {
            const count = parseInt(line.replace('SLIDE_COUNT:', '').trim());
            if (!isNaN(count)) slideCount = count;
        }
    }
    
    // Fallback: use entire response as summary if parsing failed
    if (!summary) {
        summary = response.substring(0, 200) + (response.length > 200 ? '...' : '');
    }
    
    // Determine if this is a premium file type (presentations cost extra)
    const isPremiumFileType = metadata.mimeType.includes('powerpoint') || 
        metadata.mimeType.includes('presentationml');
    
    return {
        summary,
        detectedType,
        confidence,
        keyEntities,
        suggestedQuestions,
        slideCount,
        metadata: {
            ...metadata,
            isPremiumFileType,
            tokenMultiplier: isPremiumFileType ? 1.5 : 1.0 // 50% extra for presentations
        }
    };
}

/**
 * Get quick file preview without full analysis
 */
export function getQuickFilePreview(mimeType: string, fileName: string, fileSize: number): string {
    const sizeMB = (fileSize / (1024 * 1024)).toFixed(2);
    
    if (mimeType.startsWith('image/')) {
        return `📷 Image: ${fileName} (${sizeMB}MB)`;
    } else if (mimeType === 'application/pdf') {
        return `📄 PDF Document: ${fileName} (${sizeMB}MB)`;
    } else if (
        mimeType === 'application/vnd.ms-powerpoint' || 
        mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ) {
        return `📊 PowerPoint: ${fileName} (${sizeMB}MB) ⭐ Premium`;
    } else {
        return `📎 File: ${fileName} (${sizeMB}MB)`;
    }
}

/**
 * Check if file type requires premium tokens
 */
export function isPremiumFileType(mimeType: string): boolean {
    return (
        mimeType === 'application/vnd.ms-powerpoint' || 
        mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    );
}

/**
 * Get token multiplier for file type
 */
export function getFileTypeTokenMultiplier(mimeType: string): number {
    if (isPremiumFileType(mimeType)) {
        return 1.5; // 50% extra for premium file types
    }
    return 1.0;
}

/**
 * Clear analysis cache
 */
export function clearAnalysisCache(): void {
    analysisCache.clear();
    console.log('[FileAnalyzer] Cache cleared');
}

/**
 * Extract text content from analysis for context
 */
export function extractTextContext(analysis: FileAnalysisResult, maxLength: number = 500): string {
    let context = analysis.summary;
    
    if (analysis.keyEntities.length > 0) {
        context += `\n\nKey topics: ${analysis.keyEntities.join(', ')}`;
    }
    
    if (analysis.extractedText) {
        const textPreview = analysis.extractedText.substring(0, maxLength);
        context += `\n\nContent preview: ${textPreview}${analysis.extractedText.length > maxLength ? '...' : ''}`;
    }
    
    return context;
}
